import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useClerkAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  User,
  LogOut,
  BookOpen,
  PlusCircle,
  LayoutDashboard,
  Settings,
  HelpCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserButton } from '@clerk/clerk-react';

interface TopNavBarProps {
  currentPage?:
    | 'articles'
    | 'admin'
    | 'dashboard'
    | 'pricing'
    | 'edit'
    | 'settings'
    | 'questions'
    | 'progress'
    | 'create'
    | 'users';
  onNavigateToAdmin?: () => void;
  onNavigateToArticles?: () => void;
  onNavigateToDashboard?: () => void;
  onNavigateToQuestions?: () => void;
}

const TopNavBar = ({
  currentPage = 'articles',
  onNavigateToAdmin,
  onNavigateToArticles,
  onNavigateToDashboard,
  onNavigateToQuestions,
}: TopNavBarProps) => {
  const { t } = useTranslation();
  const { user, signOut, canCreateArticle } = useAuth();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Show navbar for all users, but only show user menu if authenticated

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  const getUserInitials = () => {
    const email = user?.emailAddresses?.[0]?.emailAddress || '';
    return email.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    return (
      user?.firstName ||
      user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] ||
      'User'
    );
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-border/50 sticky top-0 z-40 shadow-sm">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <div className="flex items-center gap-8">
            <a href="/" className="flex items-center">
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-primary" />
                <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Lingua Craft
                </span>
              </div>
            </a>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-1">
              <Button
                variant={currentPage === 'articles' ? 'default' : 'ghost'}
                onClick={onNavigateToArticles}
                className="text-sm"
              >
                {t('navigation.articles')}
              </Button>
              <Button
                variant={currentPage === 'questions' ? 'default' : 'ghost'}
                onClick={
                  onNavigateToQuestions || (() => navigate('/questions'))
                }
                className="text-sm"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                {t('navigation.questions')}
              </Button>
              {user && (
                <Button
                  variant={currentPage === 'dashboard' ? 'default' : 'ghost'}
                  onClick={onNavigateToDashboard}
                  className="text-sm"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  {t('navigation.dashboard')}
                </Button>
              )}
              {canCreateArticle() && (
                <Button
                  variant={currentPage === 'admin' ? 'default' : 'ghost'}
                  onClick={onNavigateToAdmin}
                  className="text-sm"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  {t('navigation.createArticle')}
                </Button>
              )}
            </div>
          </div>

          {/* User Profile Dropdown or Auth Buttons */}
          <div className="flex items-center gap-4">
            {user ? (
              <UserButton />
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/sign-in')}
                  className="text-sm"
                >
                  {t('auth.signIn')}
                </Button>
                <Button
                  variant="default"
                  onClick={() => navigate('/sign-up')}
                  className="text-sm"
                >
                  {t('auth.signUp')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNavBar;
